import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import './plan.css';

/**
 * What the imported plan pages need around them, and nothing more.
 *
 * They came from an app whose whole shell was TanStack Query, so they assume a client is
 * already in context. The Hub has no such provider and does not need one — every other page
 * fetches with `api.get` and `useState`. Rather than wrap the entire application in a cache
 * these three screens are the only users of, the provider sits here, around them.
 *
 * That has one real consequence worth knowing: the plan cache lives and dies with this
 * boundary, so leaving the Gantt tab and coming back refetches. For a payload one project
 * wide that is the right trade — the alternative is a cache the rest of the app carries
 * around for two tabs nobody may open.
 *
 * The error boundary is here because these pages are 4,600 lines of imported code we did not
 * write. A crash in the Gantt must not take the board's own tabs with it.
 */
export function PlanBoundary({
  children,
  /**
   * Bumped by the board whenever a task changes underneath these pages — a save in the task
   * drawer, most often.
   *
   * It exists because the cache above is PRIVATE to this boundary: the drawer lives on the
   * board, outside this provider, so it has no way to invalidate a plan query. Editing a task
   * from the timeline therefore left the chart showing the old title, the old dates and the
   * old stage until something else happened to refetch — which is exactly the "it does not
   * update as we go" this fixes.
   */
  revision = 0,
}: {
  children: ReactNode;
  revision?: number;
}) {
  /**
   * Created once per mount rather than per render — a client rebuilt on every render throws
   * the cache away constantly and refetches in a loop.
   */
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // A plan is not a feed. Refetching it because a window regained focus interrupts
            // somebody mid-drag for no reason.
            refetchOnWindowFocus: false,
            staleTime: 30_000,
            retry: 1,
          },
        },
      }),
  );

  /**
   * Skipped on the first render: a fresh client has nothing stale in it, and invalidating
   * before anything is cached just fires a second fetch of what is already in flight.
   */
  const seen = useRef(revision);
  useEffect(() => {
    if (seen.current === revision) return;
    seen.current = revision;
    void client.invalidateQueries();
  }, [revision, client]);

  return (
    <ErrorBoundary variant="page">
      <QueryClientProvider client={client}>
        {/* The kit's utility classes assume its own font stack and text size. */}
        <div className="plan-root">{children}</div>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
